import React, { useState } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Alert from "@mui/material/Alert";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import BASE_URL from "../../../config/apiConfig";

export function PasswordUpdateViewPage() {
  const navigate = useNavigate();
  const email = localStorage.getItem("user_email");

  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [fieldErrors, setFieldErrors] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  // Toggle Password Visibility
  const handlePasswordVisibilityToggle = () => {
    setShowPassword((prev) => !prev);
  };

  // Strong Password Regex
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

  // Validate Form
  const validateForm = () => {
    let isValid = true;
    const errors = { newPassword: "", confirmPassword: "" };

    if (!newPassword) {
      errors.newPassword = "New password is required.";
      isValid = false;
    } else if (!passwordRegex.test(newPassword)) {
      errors.newPassword =
        "Password must be at least 8 characters long and include an uppercase, lowercase, number, and special character.";
      isValid = false;
    }

    if (!confirmPassword) {
      errors.confirmPassword = "Please confirm your password.";
      isValid = false;
    } else if (newPassword !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match.";
      isValid = false;
    }

    setFieldErrors(errors);
    return isValid;
  };

  // Handle Password Update
  const handleConfirm = async (e) => {
    e.preventDefault();
    setErrorMessage(""); // Clear previous errors

    if (!validateForm()) return;

    setLoading(true);

    try {
      const response = await axios.post(
        `${BASE_URL}/api/user/auth/reset-password`,
        {
          email,
          newPassword,
          confirmPassword,
        }
      );

      console.log("Password updated successfully:", response.data);

      // Navigate to home page after successful update
      navigate("/user/login");
    } catch (error) {
      console.error("Error updating password:", error);
      setErrorMessage(
        error.response?.data?.message ||
          "An unexpected error occurred. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        background: "linear-gradient(to right, #f8fafc, #edf2fb)",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 400,
          padding: 4,
          boxShadow: 3,
          borderRadius: 2,
          backgroundColor: "#fff",
        }}
      >
        <Typography variant="h5" align="center" sx={{ mb: 3 }}>
          Update Your Password
        </Typography>

        {/* General Error Message */}
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errorMessage}
          </Alert>
        )}

        <Box
          component="form"
          onSubmit={handleConfirm}
          display="flex"
          flexDirection="column"
        >
          <TextField
            fullWidth
            name="newPassword"
            label="New Password"
            type={showPassword ? "text" : "password"}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            error={Boolean(fieldErrors.newPassword)}
            helperText={fieldErrors.newPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handlePasswordVisibilityToggle}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
            variant="outlined"
          />
          <TextField
            fullWidth
            name="confirmPassword"
            label="Confirm Password"
            type={showPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={Boolean(fieldErrors.confirmPassword)}
            helperText={fieldErrors.confirmPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handlePasswordVisibilityToggle}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
            variant="outlined"
          />

          <LoadingButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={loading}
            sx={{
              backgroundColor: "#51833a",
              color: "#fff",
              mb: 2,
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#4a7a32",
              },
            }}
          >
            Confirm
          </LoadingButton>
        </Box>
      </Box>
    </Box>
  );
}

export default PasswordUpdateViewPage;
